import { useCallback, useRef, useState } from 'react'

import { CmsSellerFilters, CustomCmsSeller } from '../../../../domain/seller'
import ListSellers from '../ListSellers'
import SellerPageStructure from '../SellerPageStructure'
import { scrollToSeller } from '../helper'

import GoogleMapSellersFiltersMap from './GoogleMapSellersFiltersMap'
import useSellersGoogleMap from './useSellersGoogleMap'
import { GoogleMapHTMLSellersMapId } from './constants'
import { gtmPush } from '../../../utils/gtmHelpers'

type GoogleMapSellersPageProps = {
  sellers: CustomCmsSeller[]

  onSubmitFilterForm: (value: CmsSellerFilters) => void
  filters: CmsSellerFilters
}

const GoogleMapSellersPage = ({
  sellers,
  onSubmitFilterForm,
  filters,
}: GoogleMapSellersPageProps) => {
  const [selectedStore, setSelectedStore] = useState<CustomCmsSeller>()
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const { mapRef, markersRef } = useSellersGoogleMap(sellers, {
    onClickMarker: (seller) => {
      setSelectedStore(seller)
      scrollToSeller(seller)
    },
    onZoomChange(map) {
      const zoom = map.getZoom()
      if (zoom && zoom < 11 && filtersRef.current.radius) {
        onSubmitFilterForm({
          ...filtersRef.current,
          radius: undefined,
        })
      }
    },
  })

  const centerMapFromLatLng = useCallback(
    (lat: number, lng: number, zoom = 12) => {
      mapRef.current?.setCenter({ lat, lng })
      mapRef.current?.setZoom(zoom)
    },
    [mapRef],
  )

  const getPlaceFromPlaceId = (placeId: string): Promise<google.maps.places.PlaceResult | null> =>
    new Promise((resolve) => {
      if (!mapRef.current) {
        resolve(null)
        return
      }

      const placesService = new google.maps.places.PlacesService(mapRef.current)
      placesService.getDetails({ placeId }, (place) => {
        if (!place) {
          resolve(null)
          return
        }

        resolve(place)
      })
    })

  const onSelectStore = (seller: CustomCmsSeller) => {
    setSelectedStore(seller)
    gtmPush({
      event: 'select_boutique',
    })

    // center map on selected store
    centerMapFromLatLng(Number(seller.latitude), Number(seller.longitude), 15)

    // Open info window of selected store
    const marker = markersRef.current?.find((item) => item.id === seller.id)
    if (marker) {
      google.maps.event.trigger(marker, 'click')
    }
  }

  return (
    <SellerPageStructure>
      <GoogleMapSellersFiltersMap
        centerMapFromLatLng={centerMapFromLatLng}
        getPlaceFromPlaceId={getPlaceFromPlaceId}
        onSubmitFilterForm={onSubmitFilterForm}
        hasCitySearch
      />
      <div id={GoogleMapHTMLSellersMapId} className='map-sellers-googlemap' />
      <ListSellers
        sellers={sellers}
        filters={filters}
        selectedStore={selectedStore}
        onSelectStore={onSelectStore}
      />
    </SellerPageStructure>
  )
}

export default GoogleMapSellersPage
